import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button, Dialog, DialogContent, DialogTitle, StepConnector,
    FormControlLabel, makeStyles, Step, StepLabel, Stepper
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import clsx from 'clsx';
import CheckIcon from '@material-ui/icons/Check';
import Search from '@material-ui/icons/Search';
import Close from '@material-ui/icons/Close';
import { GreenCheckbox } from '../../../components/FlowConfigurationInputs';
import AutomService from '../../../services/AutomService/AutomService';
import QueuePoolService from '../../../services/QueuePoolService/QueuePoolService';
import { alertState } from "./../../../recoil-store/recoil-state";
import { useSetRecoilState } from 'recoil';

/**
 * @Description This function returns Pool dialog.
 * @Returns     Component
 * @Author      ibrahim AKIN
 */
const useColorlibStepIconStyles = makeStyles({
    root: {
        backgroundColor: '#ccc',
        zIndex: 1,
        color: '#fff',
        width: 50,
        height: 50,
        display: 'flex',
        borderRadius: '50%',
        justifyContent: 'center',
        alignItems: 'center'
    },
    active: {
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
        background: '#5AC384'
    },
    completed: {
        background: '#5AC384'
    }
});
const ColorlibConnector = withStyles({
    alternativeLabel: {
        marginLeft: -10,
        top: 22
    },
    active: {
        '& $line': {
            background: '#5AC384'
        }
    },
    completed: {
        '& $line': {
            background: '#5AC384'
        }
    },
    line: {
        backgroundColor: '#eaeaf0',
        borderRadius: 1,
        height: 3,
        border: 0
    }
})(StepConnector);

function ColorlibStepIcon(props) {
    const classes = useColorlibStepIconStyles();
    const { active, completed } = props;

    return (
        <div className={clsx(classes.root, { [classes.active]: active, [classes.completed]: completed })}>
            {completed ? <CheckIcon /> : String(props.icon)}
        </div>
    );
}

const useStyles = makeStyles(theme => ({
    root: { width: '100%', padding: 0 }
}));

function PoolDialog(props) {
    const { open, setOpen, fetchList } = props;
    const { t } = useTranslation();
    const classes = useStyles();
    const label = 'CONTENT.QUEUE-POOL.';
    const steps = ['DETAILS', 'ADD-AUTOM'];
    const data = useRef([]);
    const [step, setStep] = useState(0);
    const [list, setList] = useState([]);

    // Update queue pool page initial automs
    const [initialAutomIds, setInitialAutomIds] = useState([]);
    const [search, setSearch] = useState('');
    const [state, setState] = useState({
        name: '',
        description: '',
        list: []
    });
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        if (open.type === 'view' || open.type === 'edit') {
            getQueueDetails();
        }
    }, []);

    const getQueueDetails = async () => {
        // get queue pool detail with automs
        const res = await QueuePoolService.getQueuePoolDetail(open.id);
        if (res?.status && res.data) {
            // Atoms not belonging to queue pool were brought
            const automs = await fetchData()
            let ids = [];
            let stateData = {
                name: res.data.pool_name,
                description: res.data.description,
                list: []
            }
            // Belonging and unbelonging automs are combined.
            res.data.automs.map(autom => {
                automs.push(autom);
                stateData.list.push(autom.id);
                ids.push(autom.id);
            })
            // data to show
            setState(stateData);

            // Automs in queue saved
            setInitialAutomIds(ids);
            data.current = automs;
            // All automs that can be used
            setList(automs);
        }
    };

    const fetchData = async () => {
        const res = await AutomService.getAutomsByFilter(JSON.stringify({ qpool_id: null }));
        if (res?.status) {
            data.current = res.data;
            setList(res.data);
            return res.data;
        }
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handleChange = (e, name) => {
        setState({ ...state, [name]: e.target.value });
    };

    const handleNext = () => {
        setStep(1);
        if (open.type != 'edit') {
            fetchData();
        }
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setList(data.current.filter(a => a.name.toLowerCase().includes(e.target.value.toLowerCase())));
    };

    const handleCheck = (e, item) => {
        if (!item) {
            if (list.length !== state.list.length) {
                state.list = [];
                for (const iter of list) {
                    state.list.push(iter.id);
                }
            } else {
                state.list = [];
            }
        } else if (e.target.checked) {
            state.list?.push(item.id);
        } else {
            state.list?.splice(state.list.findIndex(id => id === item.id), 1);
        }
        setState({ ...state });
    };

    const handleDelete = async () => {
        const res = await QueuePoolService.deleteQueuePool(open.id);
        if (res?.status) {
            setOpen(null);
            fetchList();
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t(label + res.message), severity: 'error' }))
        }
    };

    const handleAdd = async () => {
        const data = { pool_name: state.name, description: state.description, automs: state.list };
        if (open.type === 'add') {
            const res = await QueuePoolService.postQueuePool(data);
            if (res?.status) {
                setOpen(null);
                fetchList();
            } else {
                setAlert(alert => ({ ...alert, show: true, message: t(label + res.message), severity: 'error' }))
            }
        } else {
            data.automs = [];
            // Automs found to add and delete in queue
            list.forEach(item => {
                const initialExist = initialAutomIds.includes(item.id)
                const selectedExist = state.list.includes(item.id);
                if (initialExist && !selectedExist) { // deleted
                    data.automs.push({ id: item.id, isAdd: false });
                } else if (!initialExist && selectedExist) {
                    data.automs.push({ id: item.id, isAdd: true });
                }
            })
            const res = await QueuePoolService.putQueuePool(open.id, data);
            if (res?.status) {
                setOpen(null);
                fetchList();
            } else {
                setAlert(alert => ({ ...alert, show: true, message: t(label + res.message), severity: 'error' }))
            }
        }
    };

    return (
        <Dialog maxWidth="xs" onClose={handleClose} fullWidth open>
            <DialogTitle>
                <div className="dialog-header variant">
                    {open.type === 'add' ? t(label + 'ADD-AUTOM') : open.type === 'edit' ? t(label + 'EDIT-AUTOM') : open.type === 'view' ? t(label + 'ALL-AUTOMS') : t(label + 'DEL-POOL')}
                    <Button onClick={handleClose}>
                        <Close fontSize="small" />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                {(open.type !== 'del' && open.type !== 'view') &&
                    <Stepper classes={{ root: classes.root }} alternativeLabel activeStep={step} connector={<ColorlibConnector />}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel StepIconComponent={ColorlibStepIcon}
                                    style={{ marginLeft: index !== steps?.length - 1 && (index === 0 ? -25 : -20) }}>
                                    <div style={{
                                        color: '#8F94A6',
                                        fontSize: 11,
                                        marginTop: -10,
                                        marginBottom: 10,
                                        fontWeight: 600
                                    }}>
                                        {t('CONTENT.WEBHOOK.' + label).toUpperCase()}
                                    </div>
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>}
                {open.type === 'add' || open.type === 'edit' ? step === 0 ?
                    <div className="vnew-alert-form" style={{ padding: 0 }}>
                        <div>
                            {t(label + 'QUEUE-POOL-NAME')}
                            <span className='mandatory-field'>*</span>
                        </div>
                        <input value={state.name} onChange={e => handleChange(e, 'name')} />
                        <div>
                            {t(label + 'DESC')}
                            <span className='mandatory-field'>*</span>
                        </div>
                        <textarea value={state.description} onChange={e => handleChange(e, 'description')}
                            rows="6" style={{ resize: 'none', width: '100%' }} />
                        <div className="vnew-alert-save">
                            <button onClick={handleNext}
                                disabled={!/\S/.test(state.name) || !/\S/.test(state.description)}>
                                {t(label + 'NEXT')}
                            </button>
                        </div>
                    </div> : <>
                        <div className="code-vsearch" style={{ margin: '0 0 20px 0' }}>
                            <Search fontSize="small" color="disabled" />
                            <input placeholder={t(label + 'SEARCH')} value={search}
                                style={{ fontSize: 12 }} onChange={handleSearch} />
                        </div>
                        {list?.length > 0 &&
                            <button className="user-detail-link" onClick={handleCheck}
                                style={{ color: '#6D6D6D', position: 'absolute', right: 20, top: 180 }}>
                                ({list?.length !== state.list.length ? t(label + 'SELECT-ALL') : t(label + 'DESELECT-ALL')})
                            </button>
                        }
                        <div className="db-scroll" style={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            {list?.map((item, itemI) => {
                                return <div key={itemI}>
                                    <FormControlLabel label={<span className="sgeneral-day">{item.name}</span>}
                                        control={<GreenCheckbox
                                            checked={!!state.list?.includes(item.id)}
                                            onChange={e => handleCheck(e, item)} size="small" />} />
                                </div>;
                            })}
                        </div>
                        <div className="vnew-alert-save">
                            <button onClick={handleAdd}>
                                {open.type === 'add' ? t(label + 'ADD-POOL') : t(label + 'EDIT-AUTOM')}
                            </button>
                        </div>
                    </> :
                    open.type === 'view' ? <>
                        {list.map((item, itemI) => {
                            if (state.list?.includes(item.id)) {
                                return <div key={itemI} className="sgeneral-day"
                                    style={{ marginBottom: 10 }}>{"- " + item.name}</div>;
                            } else {
                                return null;
                            }
                        })}
                    </> :
                        <div className="dialog-body">
                            <div style={{ whiteSpace: 'pre-wrap' }}>{t(label + 'DELETE-CONFIRM')}</div>
                            <Button onClick={handleDelete}>{t(label + 'DELETE')}</Button>
                        </div>
                }
            </DialogContent>
        </Dialog>
    );
}
export default PoolDialog;